import type { TFunction } from 'i18next';

import type { AsOf } from '@/services/types';

/**
 * The corpus-scope sentence in the reader's language.
 *
 * Every screen carries an as-of strip naming the corpus it drew from, and that
 * sentence is not decoration: several of them hold the one fact that stops
 * their screen being misread — that PAF credit is not a fine, that the TFSEE is
 * a levy and not a sanction, that CADE's window is years read from an
 * archive folder rather than dates.
 *
 * Same contract as a refusal's `reason` / `reason_key`. The English is authored
 * in the analytics layer and stays in the payload, so an export, a log line or
 * any client without i18n still gets a true sentence; `scope_key` names a
 * translation for the clients that have one.
 *
 * `defaultValue` rather than a conditional on the translation: a key that
 * exists but is untranslated in this language would otherwise render as the key
 * itself, and i18next's `fallbackLng: 'en'` would hide that by quietly
 * resolving it to English anyway.
 *
 * In `lib/` rather than beside `AsOfStrip` because one page cannot use the
 * strip — the ANATEL company profile shows the COMPANY's first and last
 * decision beside the scope, not the corpus window, and folding that into the
 * shared component would let a reader take one company's activity for the
 * coverage of the whole corpus. Both call this.
 *
 * A plain function taking `t`, not a hook: that page returns early while its
 * query is pending, so a hook here could only ever be called conditionally.
 */
export function scopeText(asOf: AsOf, t: TFunction): string {
  return asOf.scope_key
    ? t(asOf.scope_key, { defaultValue: asOf.corpus_scope })
    : asOf.corpus_scope;
}
